// Right-side slide-in drawer for full lead profile, timeline, assignment, contact log.
import { useEffect, useState } from "react";
import {
  X, Mail, Phone, MapPin, Pill, Send, DollarSign, Activity,
  Clock, User, CheckCircle2, ArrowUpRight, Building2, MessageSquare, ArrowDownLeft, PhoneMissed,
} from "lucide-react";
import { API, RED, NEON, AMBER, PURPLE, fmt$, ago, leadAge, authHeader } from "./shared";

const EVENT_COLOR = {
  "lead.created":         RED,
  "lead.updated":         AMBER,
  "assessment.completed": "#5C8DFF",
  "payment.received":     NEON,
  "provider.approved":    NEON,
  "medication.shipped":   PURPLE,
  "call.requested":       AMBER,
  "shipment.delivered":   NEON,
};

export function LeadDetailDrawer({ leadId, token, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [assignee, setAssignee] = useState("");
  const [note, setNote] = useState("");
  const [stages, setStages] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState({ title: "", due_at: "" });
  const [closeLostReason, setCloseLostReason] = useState("");

  useEffect(() => {
    if (!leadId) return;
    setData(null);
    setTasks([]);
    fetch(`${API}/leads/${leadId}/timeline`, { headers: authHeader(token) })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        setData(d);
        setAssignee(d?.lead?.owner_email || d?.lead?.assigned_to || "");
      })
      .catch(() => setData(null));
    fetch(`${API}/pipeline/tasks?lead_id=${leadId}`, { headers: authHeader(token) })
      .then((r) => (r.ok ? r.json() : []))
      .then(setTasks)
      .catch(() => setTasks([]));
  }, [leadId, token]);

  // Stages list loaded once
  useEffect(() => {
    fetch(`${API}/pipeline/stages`)
      .then((r) => r.json())
      .then((d) => setStages([...(d.stages || []), d.closed_lost].filter(Boolean)))
      .catch(() => {});
  }, []);

  if (!leadId) return null;
  const l = data?.lead;
  const age = leadAge(l?.created_at);

  async function assign() {
    if (!assignee.trim()) return;
    setBusy(true);
    await fetch(`${API}/pipeline/leads/${leadId}/assign`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader(token) },
      body: JSON.stringify({ owner_email: assignee.trim() }),
    });
    setBusy(false);
    refetch();
    onChanged && onChanged();
  }

  async function changeStage(stage, reason) {
    setBusy(true);
    if (stage === "Closed Lost") {
      const r = reason || closeLostReason || "No reason provided";
      await fetch(`${API}/pipeline/leads/${leadId}/closed-lost`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader(token) },
        body: JSON.stringify({ reason: r }),
      });
      setCloseLostReason("");
    } else {
      await fetch(`${API}/pipeline/leads/${leadId}/stage`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader(token) },
        body: JSON.stringify({ stage, reason }),
      });
    }
    setBusy(false);
    refetch();
    onChanged && onChanged();
  }

  async function addTask() {
    if (!newTask.title.trim()) return;
    setBusy(true);
    await fetch(`${API}/pipeline/tasks`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader(token) },
      body: JSON.stringify({
        lead_id: leadId,
        title: newTask.title.trim(),
        due_at: newTask.due_at || null,
        priority: "normal",
      }),
    });
    setNewTask({ title: "", due_at: "" });
    const fresh = await fetch(`${API}/pipeline/tasks?lead_id=${leadId}`, { headers: authHeader(token) }).then((r) => r.json());
    setTasks(fresh);
    setBusy(false);
  }

  async function toggleTask(t) {
    const next = t.status === "done" ? "open" : "done";
    await fetch(`${API}/pipeline/tasks/${t.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...authHeader(token) },
      body: JSON.stringify({ status: next }),
    });
    const fresh = await fetch(`${API}/pipeline/tasks?lead_id=${leadId}`, { headers: authHeader(token) }).then((r) => r.json());
    setTasks(fresh);
  }

  async function logContact() {
    setBusy(true);
    await fetch(`${API}/leads/${leadId}/contact`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader(token) },
      body: JSON.stringify({ channel: "phone", note: note || "Contacted via Command Center" }),
    });
    setBusy(false);
    setNote("");
    refetch();
    onChanged && onChanged();
  }

  function refetch() {
    fetch(`${API}/leads/${leadId}/timeline`, { headers: authHeader(token) })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setData(d));
  }

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="lead-drawer">
      <button
        type="button"
        aria-label="Close drawer"
        onClick={onClose}
        data-testid="drawer-backdrop"
        className="flex-1 bg-black/60 backdrop-blur-sm"
      />
      <aside
        className="w-full sm:w-[460px] md:w-[540px] bg-[#0A0B10] border-l border-white/10 h-full overflow-y-auto"
        data-testid="drawer-panel"
      >
        {/* Header */}
        <header className="sticky top-0 z-10 bg-[#0A0B10]/95 backdrop-blur border-b border-white/10 px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.18em] font-bold text-white/45">LEAD DETAIL</p>
            <p className="font-heavy text-white text-[18px] leading-tight mt-0.5 truncate">
              {[l?.first_name, l?.last_name].filter(Boolean).join(" ") || l?.email || "Loading…"}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="drawer-close"
            className="w-9 h-9 flex items-center justify-center rounded-sm border border-white/15 text-white/70 hover:text-white hover:border-[#FF2A3C]"
          >
            <X size={16} />
          </button>
        </header>

        {!l && (
          <div className="px-5 py-16 text-center text-white/45 text-[13px]">Loading lead…</div>
        )}

        {l && (
          <div className="px-5 py-5 space-y-5">
            {/* Age + brand + stage chips */}
            <div className="flex flex-wrap items-center gap-2">
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-[10.5px] font-extrabold tracking-[0.14em]"
                style={{ borderColor: age.color, color: age.color, background: `${age.color}10` }}
                data-testid="drawer-lead-age"
                title={`Age: ${age.text}`}
              >
                <Clock size={11} /> {age.label} · {age.text}
              </span>
              <span className="inline-flex px-2 py-1 rounded-sm border border-white/15 text-[10px] font-bold tracking-[0.12em] text-white">
                {(l.brand || "BestRx").toUpperCase()}
              </span>
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-[10.5px] font-extrabold tracking-[0.12em]"
                style={{ borderColor: RED, color: RED, background: `${RED}15` }}
                data-testid="drawer-stage-badge"
                title="Pipeline stage"
              >
                <Activity size={11} /> {(l.stage || "New Lead").toUpperCase()}
              </span>
              {(l.owner_email || l.assigned_to) && (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-white/15 text-[10px] font-bold text-white/80">
                  <User size={11} /> {l.owner_email || l.assigned_to}
                </span>
              )}
            </div>

            {/* Contact + revenue grid */}
            <div className="grid grid-cols-2 gap-2.5">
              <InfoCell Icon={Mail}     label="EMAIL"        value={l.email} mono />
              <InfoCell Icon={Phone}    label="PHONE"        value={l.phone} mono />
              <InfoCell Icon={MapPin}   label="CITY"         value={l.city || "—"} />
              <InfoCell Icon={Pill}     label="INTEREST"     value={l.interest || "—"} />
              <InfoCell Icon={Pill}     label="MEDICATION"   value={l.medication_slug || "—"} mono />
              <InfoCell Icon={CheckCircle2} label="ASSESSMENT" value={l.assessment_answers ? "Completed" : "Not Started"} accent={l.assessment_answers ? NEON : undefined} />
              <InfoCell Icon={Building2} label="SOURCE"      value={l.source || "—"} mono small />
              <InfoCell Icon={Building2} label="LANDING"     value={l.landing_page || "—"} mono small />
              <InfoCell Icon={DollarSign} label="REVENUE"    value={fmt$(data?.revenue_cents)} accent={NEON} />
            </div>

            {/* Assessment answers (V1 — the data captured by the lead modal) */}
            {l.assessment_answers && (
              <section className="rounded-sm border border-white/10 bg-black/40 p-4 space-y-2"
                data-testid="drawer-assessment-answers">
                <p className="font-heavy text-[12px] tracking-[0.14em] text-white/65 uppercase">Assessment Answers</p>
                <div className="grid grid-cols-2 gap-1.5 text-[12px]">
                  {Object.entries(l.assessment_answers).map(([k, v]) => v && (
                    <div key={k} className="flex justify-between gap-3 px-2 py-1 bg-black/40 rounded-sm">
                      <span className="text-white/50 tracking-[0.10em] uppercase text-[10.5px]">{k.replace(/_/g, " ")}</span>
                      <span className="text-white font-mono text-[11.5px] truncate">{String(v)}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Pipeline Stage controls + history */}
            <section className="rounded-sm border border-white/10 bg-black/40 p-4 space-y-3"
              data-testid="drawer-stage-section">
              <div className="flex items-center justify-between">
                <p className="font-heavy text-[12px] tracking-[0.14em] text-white/65 uppercase">Pipeline Stage</p>
                <span className="text-[10px] tracking-[0.12em] font-bold text-white/40">{(l.stage_history || []).length} transitions</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {stages.filter((s) => s !== "Closed Lost").map((s) => {
                  const isCurrent = s === l.stage;
                  return (
                    <button
                      key={s}
                      onClick={() => !isCurrent && changeStage(s, "Manual transition via drawer")}
                      disabled={busy || isCurrent}
                      data-testid={`drawer-stage-${s.toLowerCase().replace(/\s+/g, "-")}`}
                      className={
                        "px-2.5 py-1 text-[10px] font-extrabold tracking-[0.10em] uppercase border " +
                        (isCurrent
                          ? "border-[#FF2A3C] text-white bg-[#FF2A3C]/15"
                          : "border-white/15 text-white/65 hover:text-white hover:border-white/40")
                      }
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2 pt-2 border-t border-white/5">
                <input
                  value={closeLostReason}
                  onChange={(e) => setCloseLostReason(e.target.value)}
                  placeholder="Reason for Closed Lost"
                  data-testid="drawer-closed-lost-reason"
                  className="flex-1 bg-black border border-white/15 px-3 py-2 text-white font-mono text-[12px] focus:border-[#6B7280] focus:outline-none"
                />
                <button
                  onClick={() => changeStage("Closed Lost")}
                  disabled={busy || !closeLostReason.trim() || l.stage === "Closed Lost"}
                  data-testid="drawer-closed-lost-btn"
                  className="px-3 py-2 text-[10.5px] font-extrabold tracking-[0.12em] uppercase border border-[#6B7280] text-white/80 hover:bg-white/5 disabled:opacity-40"
                >
                  Close Lost
                </button>
              </div>
              {/* Stage history */}
              {(l.stage_history || []).length > 0 && (
                <div className="pt-2 border-t border-white/5 max-h-[180px] overflow-y-auto"
                  data-testid="drawer-stage-history">
                  <ul className="space-y-1.5">
                    {(l.stage_history || []).slice().reverse().map((h, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-[11px]">
                        <span className="text-white/40 font-mono text-[10px] min-w-[64px]">{ago(h.at)}</span>
                        <span className="text-white/60 truncate">
                          {h.from || "·"} → <span className="text-white font-bold">{h.to}</span>
                          {h.source_event && <span className="text-white/35 ml-1">[{h.source_event}]</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            {/* Follow-up Tasks */}
            <section className="rounded-sm border border-white/10 bg-black/40 p-4 space-y-3"
              data-testid="drawer-tasks-section">
              <div className="flex items-center justify-between">
                <p className="font-heavy text-[12px] tracking-[0.14em] text-white/65 uppercase">Follow-Up Tasks</p>
                <span className="text-[10px] tracking-[0.12em] font-bold text-white/40">{tasks.filter((t) => t.status === "open").length} open</span>
              </div>
              <div className="flex gap-2">
                <input
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder="Task title (e.g. Day-7 follow-up call)"
                  data-testid="drawer-task-title"
                  className="flex-1 bg-black border border-white/15 px-3 py-2 text-white font-mono text-[12px] focus:border-[#FF2A3C] focus:outline-none"
                />
                <input
                  type="datetime-local"
                  value={newTask.due_at}
                  onChange={(e) => setNewTask({ ...newTask, due_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                  data-testid="drawer-task-due"
                  className="bg-black border border-white/15 px-2 py-2 text-white font-mono text-[11px] focus:border-[#FF2A3C] focus:outline-none"
                />
                <button
                  onClick={addTask}
                  disabled={busy || !newTask.title.trim()}
                  data-testid="drawer-task-add"
                  className="px-3 py-2 text-[10.5px] font-extrabold tracking-[0.12em] uppercase border"
                  style={{ background: RED, borderColor: RED, color: "white" }}
                >
                  Add
                </button>
              </div>
              {tasks.length === 0 ? (
                <p className="text-[11px] text-white/35 italic">No follow-up tasks yet.</p>
              ) : (
                <ul className="space-y-1.5 max-h-[180px] overflow-y-auto">
                  {tasks.map((t) => (
                    <li key={t.id}
                      data-testid={`drawer-task-${t.id.slice(0, 8)}`}
                      className="flex items-center gap-2 px-2 py-1.5 bg-black/40 rounded-sm">
                      <input
                        type="checkbox"
                        checked={t.status === "done"}
                        onChange={() => toggleTask(t)}
                        data-testid={`drawer-task-toggle-${t.id.slice(0, 8)}`}
                        className="accent-[#FF2A3C]"
                      />
                      <span className={"flex-1 text-[12px] " + (t.status === "done" ? "line-through text-white/35" : "text-white")}>
                        {t.title}
                      </span>
                      {t.due_at && (
                        <span className="text-[10px] text-white/40 font-mono">
                          {new Date(t.due_at).toLocaleDateString()}
                        </span>
                      )}
                      {t.priority === "high" && (
                        <span className="text-[9px] tracking-[0.12em] font-extrabold uppercase text-[#FF2A3C]">!HIGH</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Assignment + Contact log */}
            <section className="rounded-sm border border-white/10 bg-black/40 p-4 space-y-3">
              <p className="font-heavy text-[12px] tracking-[0.14em] text-white/65 uppercase">Assign + Log Contact</p>
              <div className="flex gap-2">
                <input
                  value={assignee}
                  onChange={(e) => setAssignee(e.target.value)}
                  placeholder="rep@bestrx.online"
                  data-testid="drawer-assign-input"
                  className="flex-1 bg-black border border-white/15 px-3 py-2 text-white font-mono text-[12px] focus:border-[#FF2A3C] focus:outline-none"
                />
                <button
                  onClick={assign}
                  disabled={busy || !assignee.trim()}
                  data-testid="drawer-assign-btn"
                  className="px-3 py-2 text-[11px] font-extrabold tracking-[0.12em] uppercase border"
                  style={{ background: RED, borderColor: RED, color: "white" }}
                >
                  Assign
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Contact note (called, left VM…)"
                  data-testid="drawer-note-input"
                  className="flex-1 bg-black border border-white/15 px-3 py-2 text-white font-mono text-[12px] focus:border-[#42FFB0] focus:outline-none"
                />
                <button
                  onClick={logContact}
                  disabled={busy}
                  data-testid="drawer-contact-btn"
                  className="px-3 py-2 text-[11px] font-extrabold tracking-[0.12em] uppercase border"
                  style={{ background: NEON, borderColor: NEON, color: "black" }}
                >
                  Log Call
                </button>
              </div>
              {l.last_contact_date && (
                <p className="text-[10.5px] text-white/45 inline-flex items-center gap-1.5">
                  <CheckCircle2 size={11} color={NEON} /> Last contacted {ago(l.last_contact_date)}
                </p>
              )}
            </section>

            {/* Payments */}
            <Section title="Payments" Icon={DollarSign} accent={NEON} count={data?.payments?.length}>
              {(data?.payments || []).length === 0 && <Empty label="No payments yet." />}
              {(data?.payments || []).map((p) => (
                <Row key={p.id}>
                  <span className="font-heavy text-[13px]" style={{ color: NEON }}>{fmt$(p.amount_cents)}</span>
                  <span className="text-white/65 text-[11px]">{p.processor}</span>
                  <span className="text-white/45 text-[11px] truncate">{p.description}</span>
                  <span className="text-white/45 text-[10.5px] font-mono">{ago(p.created_at)}</span>
                </Row>
              ))}
            </Section>

            {/* Assessments */}
            <Section title="Assessments" Icon={CheckCircle2} accent="#5C8DFF" count={data?.assessments?.length}>
              {(data?.assessments || []).length === 0 && <Empty label="No assessments yet." />}
              {(data?.assessments || []).map((a) => (
                <Row key={a.id}>
                  <span className="text-white text-[12px]">{a.interest || "Assessment"}</span>
                  <span className="text-white/65 text-[10.5px] font-mono truncate col-span-2">
                    {Object.entries(a.answers || {}).map(([k, v]) => `${k}: ${v}`).join(" · ") || "—"}
                  </span>
                  <span className="text-white/45 text-[10.5px] font-mono">{ago(a.created_at)}</span>
                </Row>
              ))}
            </Section>

            {/* Shipments */}
            <Section title="Shipments" Icon={Send} accent={PURPLE} count={data?.shipments?.length}>
              {(data?.shipments || []).length === 0 && <Empty label="No shipments yet." />}
              {(data?.shipments || []).map((s) => (
                <Row key={s.id}>
                  <span className="text-white text-[12px] font-mono">{s.tracking_number}</span>
                  <span className="text-white/65 text-[11px]">{s.carrier}</span>
                  <span className="text-white/45 text-[10.5px]">{(s.medications || []).join(", ")}</span>
                  <span className="text-white/45 text-[10.5px] font-mono">{ago(s.created_at)}</span>
                </Row>
              ))}
            </Section>

            {/* SMS history */}
            <Section title="SMS History" Icon={MessageSquare} accent="#5C8DFF" count={data?.messages?.length}>
              {(data?.messages || []).length === 0 && <Empty label="No SMS history with this lead yet." />}
              {(data?.messages || []).map((m) => (
                <div key={m.id} className="px-3 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    {m.direction === "outbound"
                      ? <ArrowUpRight size={10} color={RED} />
                      : <ArrowDownLeft size={10} color={NEON} />}
                    <span className="text-[9.5px] tracking-[0.12em] font-extrabold uppercase"
                      style={{ color: m.direction === "outbound" ? RED : NEON }}>
                      {m.direction}
                    </span>
                    {m.user_email === "auto-reply" && (
                      <span className="text-[9px] tracking-[0.12em] font-extrabold uppercase" style={{ color: PURPLE }}>auto</span>
                    )}
                    <span className="ml-auto text-[10px] text-white/45 font-mono">{ago(m.created_at)}</span>
                  </div>
                  <p className="text-[11.5px] text-white/85 leading-relaxed">{m.body}</p>
                </div>
              ))}
            </Section>

            {/* Call history */}
            <Section title="Call History" Icon={Phone} accent={AMBER} count={data?.calls?.length}>
              {(data?.calls || []).length === 0 && <Empty label="No calls logged with this lead yet." />}
              {(data?.calls || []).map((c) => {
                const dur = c.duration_sec ? `${Math.floor(c.duration_sec/60)}:${String(c.duration_sec%60).padStart(2,"0")}` : "—";
                const StatusIcon = c.status === "missed" ? PhoneMissed : Phone;
                const statusColor = c.status === "missed" ? RED : c.status === "completed" ? NEON : AMBER;
                return (
                  <div key={c.id} className="px-3 py-2 grid grid-cols-4 gap-2 items-center">
                    <span className="inline-flex items-center gap-1 text-[10.5px] font-extrabold uppercase tracking-[0.12em]"
                      style={{ color: statusColor }}>
                      <StatusIcon size={11} /> {c.status}
                    </span>
                    <span className="col-span-2 text-[11px] text-white/65 font-mono truncate">
                      {c.direction === "outbound" ? `→ ${c.to}` : `← ${c.from}`}
                      {(c.notes?.length || 0) > 0 && <span className="text-white/45"> · {c.notes.length} note{c.notes.length === 1 ? "" : "s"}</span>}
                    </span>
                    <span className="text-right text-[10.5px] text-white/45 font-mono">{dur}</span>
                  </div>
                );
              })}
            </Section>

            {/* Timeline / Events */}
            <Section title="Event Timeline" Icon={Activity} accent={RED} count={data?.events?.length}>
              {(data?.events || []).length === 0 && <Empty label="No events recorded yet." />}
              {(data?.events || []).slice().reverse().map((e) => {
                const c = EVENT_COLOR[e.event] || "white";
                return (
                  <div key={e.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-l-2" style={{ borderColor: c }}>
                    <span className="col-span-4 font-heavy text-[11px] tracking-tight" style={{ color: c }}>{e.event}</span>
                    <span className="col-span-5 text-white/65 text-[10.5px] font-mono truncate">
                      {e.payload?.email || e.payload?.lead_id || e.payload?.tracking_number || "—"}
                    </span>
                    <span className="col-span-3 text-right text-white/45 text-[10.5px] font-mono">{ago(e.created_at)}</span>
                  </div>
                );
              })}
            </Section>

            {/* Audit Log */}
            <Section title="Audit Log" Icon={User} accent="white" count={data?.audit?.length}>
              {(data?.audit || []).length === 0 && <Empty label="No audit entries yet." />}
              {(data?.audit || []).slice().reverse().map((a) => (
                <Row key={a.id}>
                  <span className="font-heavy text-[11px] text-white">{a.action}</span>
                  <span className="col-span-2 text-white/65 text-[10.5px]">{a.actor_email}</span>
                  <span className="text-white/45 text-[10.5px] font-mono">{ago(a.created_at)}</span>
                </Row>
              ))}
            </Section>

            <a
              href={`tel:${l.phone}`}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-[12px] font-extrabold tracking-[0.14em] uppercase text-white"
              style={{ background: RED, boxShadow: "0 10px 24px rgba(255,42,60,0.30)" }}
              data-testid="drawer-call-now"
            >
              Call {l.phone} Now <ArrowUpRight size={14} strokeWidth={2.8} />
            </a>
          </div>
        )}
      </aside>
    </div>
  );
}

function InfoCell({ Icon, label, value, mono, small, accent }) {
  return (
    <div className="border border-white/10 bg-black/40 px-3 py-2 rounded-sm">
      <p className="text-[9.5px] tracking-[0.16em] font-bold text-white/45 inline-flex items-center gap-1.5">
        <Icon size={10} /> {label}
      </p>
      <p
        className={`text-white mt-1 ${mono ? "font-mono" : "font-extrabold"} ${small ? "text-[10.5px]" : "text-[12.5px]"} truncate`}
        style={accent ? { color: accent } : undefined}
      >
        {value}
      </p>
    </div>
  );
}

function Section({ title, Icon, accent, count, children }) {
  return (
    <section className="rounded-sm border border-white/10 bg-black/40">
      <header className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between">
        <p className="font-heavy text-[12px] inline-flex items-center gap-2" style={{ color: accent }}>
          <Icon size={12} /> {title.toUpperCase()}
        </p>
        {typeof count === "number" && (
          <span className="text-[10px] tracking-[0.16em] font-bold text-white/45">{count}</span>
        )}
      </header>
      <div className="divide-y divide-white/5 max-h-[260px] overflow-y-auto">{children}</div>
    </section>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-4 gap-2 px-3 py-2 items-center">{children}</div>;
}

function Empty({ label }) {
  return <p className="px-4 py-5 text-center text-white/40 text-[11.5px]">{label}</p>;
}
